import { getCategoryFields } from '../../data/categoryFields';
import {
  createProductDoc,
  getUserRef,
  productsRef,
} from '../../firebase/collections';
import { Firebase } from '../../firebase/config';
import { canProceed, RATE_LIMITS } from '../../utils/rateLimit';
import {
  validateAdDescription,
  validateAdTitle,
  validatePrice,
  validateRequired,
} from '../../utils/validation';
import { isRewardCategory, TOKEN_KINDS } from '../../utils/tokenConfig';

export function buildProductData(values, user, imageUrls, status, seller = {}, lang) {
  const payload = {
    name: values.name,
    category: values.category,
    subcategory: values.subcategory || '',
    price: Number(values.price) || 0,
    // "Bonuses" items are priced in the reward token, everything else in CXBT
    paymentToken: isRewardCategory(values.category)
      ? TOKEN_KINDS.REWARD
      : TOKEN_KINDS.STANDARD,
    description: values.description || '',
    url: imageUrls?.[0] || '',
    images: imageUrls || [],
    userId: user.uid,
    status,
    negotiable: Boolean(values.negotiable),
    listedBy: values.listedBy || '',
    adType: values.adType || 'for_sale',
    priceType: values.priceType || 'fixed',
    tags: Array.isArray(values.tags) ? values.tags : [],
    videoUrl: values.videoUrl || '',
    returnPolicy: values.returnPolicy || 'no_returns',
    paymentMethods: Array.isArray(values.paymentMethods)
      ? values.paymentMethods
      : [],
    contactPreference: values.contactPreference || 'both',
    availableFrom: values.availableFrom || null,
    location:
      values.location && (values.location.city || values.location.state)
        ? values.location
        : {},
    delivery:
      values.delivery && typeof values.delivery === 'object'
        ? {
            ...values.delivery,
            shippingCost:
              values.delivery.shippingCost === '' ||
              values.delivery.shippingCost == null
                ? 0
                : Number(values.delivery.shippingCost) || 0,
          }
        : {},
    warranty:
      values.warranty && typeof values.warranty === 'object'
        ? values.warranty
        : {},
    extra: (() => {
      if (!values.extra || Object.keys(values.extra).length === 0) return {};
      const extraClean = {};
      Object.entries(values.extra).forEach(([k, v]) => {
        if (v !== '' && v != null) extraClean[k] = v;
      });
      return extraClean;
    })(),
    condition: values.condition || '',
  };
  // Whitelisted sellers get auto-whitelisted products
  payload.whitelisted = Boolean(seller.whitelisted);
  payload.moderationStatus = seller.whitelisted ? 'approved' : 'pending';
  payload.originalLanguage = lang || 'en';

  return createProductDoc(payload, {
    name:
      seller.name || user.displayName || user.email?.split('@')[0] || 'User',
    avatar: seller.avatar || user.photoURL || '',
    verified: Boolean(seller.verified),
    badgeLevel: seller.badgeLevel || 'new',
  });
}

function scrollToFirstError(errors) {
  // Small delay to let React render the error messages first
  setTimeout(() => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return;
    // Try to find the first error element by its input id
    for (const key of errorKeys) {
      const cleanKey = key.replace('extra.', '');
      // Location fields have special IDs (ad-location-state / ad-location-city)
      if (cleanKey === 'location') {
        const stateEl = document.getElementById('ad-location-state');
        const cityEl = document.getElementById('ad-location-city');
        const target = stateEl && !stateEl.value ? stateEl : cityEl;
        if (target) {
          target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          target.focus({ preventScroll: true });
          return;
        }
      }
      const el = document.getElementById(`ad-${cleanKey}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
        return;
      }
    }
    // Fallback: scroll to the first visible error message
    const errorEl = document.querySelector('.cf-error');
    if (errorEl) {
      errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, 100);
}

export function handleSubmit({
  values,
  user,
  addToast,
  history,
  setLoading,
  setErrors,
  t,
  lang,
}) {
  if (!canProceed('adCreate', RATE_LIMITS.AD_CREATE_MS)) {
    addToast(t('createAd.pleaseWaitBeforePosting'), 'info');
    return;
  }
  const err = {};

  // 1. Category (required)
  if (validateRequired(values.category, 'Category'))
    err.category = t('createAd.pleaseSelectCategory');

  // 2. Title (required, min 3, max 70)
  const tErr = validateAdTitle(values.name);
  if (tErr) err.name = tErr;

  // 3. Description (required, min 10, max 4000)
  const dErr = validateAdDescription(values.description);
  if (dErr) err.description = dErr;

  // 4. Price (required, valid number)
  const pErr = validatePrice(values.price);
  if (pErr) err.price = pErr;

  // 5. Condition (required)
  if (!values.condition?.trim()) err.condition = t('createAd.pleaseSelectCondition');

  // 6. Images (at least 1)
  const filesToUpload = values.images.filter((i) => i instanceof File);
  const existingUrls = values.images.filter((i) => typeof i === 'string');
  if (filesToUpload.length === 0 && existingUrls.length === 0)
    err.images = t('createAd.pleaseAddImage');

  // 7. Location (both state and city required)
  if (!values.location?.state || !values.location?.city)
    err.location = t('createAd.pleaseSelectLocation');

  // 8. Validate ALL category-specific required fields
  const catFields = getCategoryFields(values.category);
  const extra = values.extra || {};
  catFields.forEach((f) => {
    if (f.required) {
      const val = extra[f.key];
      if (val === undefined || val === null || String(val).trim() === '') {
        const fieldLabel = t(`adFields.${f.key}`, {
          defaultValue: f.label.replace(/\s*\*/, ''),
        });
        err[`extra.${f.key}`] = t('createAd.isRequired', {
          defaultValue: `{{field}} is required`,
          field: fieldLabel,
        });
      }
    }
  });

  setErrors(err);
  if (Object.keys(err).length > 0) {
    const count = Object.keys(err).length;
    addToast(
      t('createAd.fixErrors', { count }),
      'error'
    );
    scrollToFirstError(err);
    return;
  }

  setLoading(true);
  const uploadPromises = filesToUpload.map((file) =>
    Firebase.storage()
      .ref(`/image/${user.uid}_${Date.now()}_${file.name}`)
      .put(file)
      .then((snap) => snap.ref.getDownloadURL())
  );
  Promise.all([Promise.all(uploadPromises), getUserRef(user.uid).get()])
    .then(([urls, userSnap]) => {
      const seller = userSnap.exists ? userSnap.data() : {};
      const data = buildProductData(values, user, urls, 'active', seller, lang);
      return productsRef().add(data).then((docRef) => ({ docRef, seller }));
    })
    .then(({ seller }) => {
      // Show moderation notice — whitelisted sellers skip moderation
      if (seller.whitelisted) {
        addToast(t('createAd.adCreated'), 'success');
      } else {
        addToast(t('createAd.adCreatedModeration'), 'info');
      }
      history.push('/');
    })
    .catch(() => {
      setLoading(false);
      addToast(t('createAd.failedToPostAd'), 'error');
    });
}

export function handleSaveDraft({
  values,
  user,
  addToast,
  history,
  setLoading,
  setErrors,
  t,
  lang,
}) {
  const err = {};
  if (!values.name && !values.category) {
    if (!values.name) err.name = t('createAd.addTitleToSaveDraft');
    if (!values.category) err.category = t('createAd.selectCategoryToSaveDraft');
  }
  if (setErrors) setErrors(err);
  if (Object.keys(err).length > 0) {
    addToast(t('createAd.addTitleOrCategoryToSaveDraft'), 'error');
    scrollToFirstError(err);
    return;
  }

  setLoading(true);
  const filesToUpload = values.images.filter((i) => i instanceof File);
  const uploadPromises = filesToUpload.length
    ? Promise.all(
        filesToUpload.map((file) =>
          Firebase.storage()
            .ref(`/image/${user.uid}_${Date.now()}_${file.name}`)
            .put(file)
            .then((snap) => snap.ref.getDownloadURL())
        )
      )
    : Promise.resolve([]);
  Promise.all([uploadPromises, getUserRef(user.uid).get()])
    .then(([urls, userSnap]) => {
      const seller = userSnap.exists ? userSnap.data() : {};
      const data = buildProductData(
        values,
        user,
        urls.length ? urls : [],
        'draft',
        seller,
        lang
      );
      return productsRef().add(data);
    })
    .then(() => {
      setLoading(false);
      addToast(t('createAd.draftSaved'), 'success');
      history.push('/dashboard');
    })
    .catch((e) => {
      setLoading(false);
      addToast(t('createAd.failedToSaveDraft'), 'error');
      console.error('Draft save error:', e);
    });
}
